/**
 * Budget Pay — Live TV COA entrypoint (Live TV theme extension assets/, flat filenames).
 * Scoped to [data-live-tv-page-id]; no @theme/events. Imports ./budget-pay-bp-cart-core.js.
 * PDP cart logic remains in the Budget Pay app (budget-pay-cart.js).
 */

import {
  CART_UPDATE_SYNC_DEBOUNCE_MS,
  LTV_BP_DEBUG_KEY,
  TOGGLE_SELECTOR,
  clearBudgetPayError,
  isLtvBpDebugEnabled,
  ltvBpDebugLog,
  mergeBpSellingPlansFromDomIntoBody,
  getSellingPlanGid,
  removeSellingPlanInputFromForm,
  showBudgetPayError,
  syncBudgetPayStateWithContext,
  getEligibilityFromDataset,
} from './budget-pay-bp-cart-core.js';

const LIVE_TV_ROOT_SELECTOR = '[data-live-tv-page-id]';

function parseVariantMetaMap(article) {
  const script = article.querySelector('script[type="application/json"][data-ltv-bp-variant-by-id]');
  if (!script?.textContent?.trim()) return {};
  try {
    const v = JSON.parse(script.textContent);
    return v != null && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}

/**
 * The auction price of the option the shopper has actually selected, in cents.
 *
 * `data-line-price` is the AMS price for that SKU in the shop's currency unit — stamped by the
 * server at render from the currently_on_air metafield and rewritten by every live tick, so it
 * matches the figure on the card. The Shopify variant price does not: a lot trades far below its
 * catalogue price for the whole auction, and measuring credit against the catalogue figure rejects
 * shoppers who are comfortably within their limit.
 *
 * Later selects narrow the choice (GLA -> MLA), so the last one carrying a price wins.
 *
 * @param {HTMLFormElement | null} form
 * @returns {number|null} null when no selected option carries a usable line price
 */
function readSelectedLinePriceCents(form) {
  if (!form) return null;
  const selects = form.querySelectorAll(
    'select[data-gla-select], select[data-mla-select], select[data-variant-select]',
  );
  let cents = null;
  for (const select of selects) {
    const raw = select.options[select.selectedIndex]?.getAttribute('data-line-price');
    if (raw == null || String(raw).trim() === '') continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) cents = Math.round(n * 100);
  }
  return cents;
}

class LiveTvBudgetPayToggle {
  /**
   * @param {HTMLElement} toggleEl
   * @param {HTMLElement} articleEl
   */
  constructor(toggleEl, articleEl) {
    this.el = toggleEl;
    this.articleEl = articleEl;
    this.button = toggleEl.querySelector('[data-budget-pay-toggle] button, .budget-pay-toggle__button');
    this.active = false;
    this.sellingPlanGid = null;
    this.inputId =
      articleEl.dataset?.bpSellingPlanInputId || `budget-pay-selling-plan-ltv-${articleEl.dataset.productId || 'coa'}`;

    if (!this.button) return;

    toggleEl._budgetPayToggleInstance = this;
    this.button.addEventListener('click', () => {
      void this.toggle();
    });
    this.resolveSellingPlan();
    // Our code never sets `hidden` on the toggle (only inline display); an external
    // script has been observed hiding it once after load. Undo any such foreign hide.
    this._hiddenGuard = new MutationObserver(() => {
      if (this.el.hasAttribute('hidden') && this.sellingPlanGid) {
        this.el.removeAttribute('hidden');
        console.warn('[BudgetPay][LTV] removed foreign hidden attribute on toggle');
      }
    });
    this._hiddenGuard.observe(this.el, { attributes: true, attributeFilter: ['hidden'] });
    /* A click that landed before this instance existed — live-tv-card-forms.js parks it here
       because the toggle is visible from first paint but only wired once this module resolves. */
    if (this.el.dataset.ltvBpPendingClick === '1') {
      delete this.el.dataset.ltvBpPendingClick;
      if (this.sellingPlanGid) {
        ltvBpDebugLog('replaying queued toggle click');
        void this.toggle();
      }
    }
    ltvBpDebugLog('toggle init', {
      productHandle: articleEl.dataset?.productHandle,
      inputId: this.inputId,
      minInstallments: this.minInstallments,
      sellingPlanGid: this.sellingPlanGid,
      formFound: !!this.findProductForm(),
    });
  }

  get minInstallments() {
    return parseInt(this.el.dataset.minInstallments || '0', 10);
  }

  getSyncContext() {
    const self = this;
    return {
      toggleEl: self.el,
      getProductHandle: () => self.articleEl.dataset.productHandle || '',
      getForm: () => self.findProductForm(),
      getMinInstallments: () => self.minInstallments,
      getSelectedQuantity: () => {
        const form = self.findProductForm();
        if (!form) return 1;
        const qtyInput = form.querySelector('input[name="quantity"]');
        const qty = Number(qtyInput?.value ?? 1);
        if (!Number.isFinite(qty) || qty < 1) return 1;
        return Math.floor(qty);
      },
      /* Live auction price first; the Shopify variant price is only a last resort, for cards that
         carry neither a priced option nor a live price (see readSelectedLinePriceCents). */
      getProductPriceCents: () => {
        const linePrice = readSelectedLinePriceCents(self.findProductForm());
        if (linePrice != null) return linePrice;
        const live = Number(self.el.dataset.livePriceCents ?? 0);
        if (Number.isFinite(live) && live > 0) return Math.round(live);
        const explicit = Number(self.el.dataset.productPriceCents ?? 0);
        if (Number.isFinite(explicit) && explicit > 0) return Math.round(explicit);
        return 0;
      },
      getEligibilityConfig: () => getEligibilityFromDataset(self.el.dataset),
      getSellingPlanInputId: () => self.inputId,
      onSellingPlanApplied: (gid) => {
        self.sellingPlanGid = gid;
      },
      domRoot: self.articleEl,
    };
  }

  resolveSellingPlan() {
    const count = this.minInstallments;
    if (!count) return;

    const handle = this.articleEl.dataset.productHandle || '';
    this.sellingPlanGid = getSellingPlanGid(count, handle, this.articleEl);

    if (!this.sellingPlanGid) {
      this.el.style.display = 'none';
      ltvBpDebugLog('resolveSellingPlan: hidden (no plan)', { count, handle });
    } else {
      this.el.style.display = '';
      this.el.removeAttribute('hidden');
      ltvBpDebugLog('resolveSellingPlan: ok', { count, handle, sellingPlanGid: this.sellingPlanGid });
    }
  }

  async toggle() {
    if (this.active) {
      this.active = false;
      this.button.setAttribute('aria-pressed', 'false');
      this.el.removeAttribute('data-active');
      this.removeSellingPlanInput();
      clearBudgetPayError(this.el);
      ltvBpDebugLog('toggle: off');
      return;
    }

    if (!this.sellingPlanGid) {
      console.warn('[BudgetPay][LTV] toggle ignored: no sellingPlanGid', {
        minInstallments: this.minInstallments,
        productHandle: this.articleEl.dataset?.productHandle,
      });
      return;
    }

    this.active = true;
    this.button.setAttribute('aria-pressed', 'true');
    this.el.setAttribute('data-active', '');
    this.button.disabled = true;

    ltvBpDebugLog('toggle: on (sync starting)', {
      sellingPlanGid: this.sellingPlanGid,
      form: this.findProductForm()?.id,
    });
    try {
      await syncBudgetPayStateWithContext(this.getSyncContext());
      ltvBpDebugLog('toggle: sync succeeded', {
        active: this.active,
        formSellingPlan: this.findProductForm()?.querySelector('input[name="selling_plan"]')?.value,
      });
    } catch (err) {
      console.error('[BudgetPay][LTV] sync', err);
      this.active = false;
      this.button.setAttribute('aria-pressed', 'false');
      this.el.removeAttribute('data-active');
      this.removeSellingPlanInput();
      const message =
        err instanceof Error ? err.message : 'Budget Pay could not be enabled. Try again.';
      showBudgetPayError(message, this.el);
    } finally {
      this.button.disabled = false;
    }
  }

  removeSellingPlanInput() {
    removeSellingPlanInputFromForm(this.findProductForm(), this.inputId);
  }

  findProductForm() {
    let el = this.el;
    while (el && el !== document.body) {
      const form = el.querySelector('form[action*="/cart/add"]');
      if (form) return form;
      el = el.parentElement;
    }
    const fallback = this.articleEl.querySelector('form[action*="/cart/add"]');
    if (!fallback && isLtvBpDebugEnabled()) {
      ltvBpDebugLog('findProductForm: no form', {
        articleProductId: this.articleEl.dataset?.productId,
      });
    }
    return fallback;
  }

  /**
   * @param {string} variantId
   */
  onCoaVariantChange(variantId) {
    const map = parseVariantMetaMap(this.articleEl);
    const row = map[String(variantId)] ?? map[variantId];
    const wasActive = this.active;

    if (row && typeof row === 'object') {
      this.el.dataset.variantId = String(variantId);
      if (Number.isFinite(row.price) && row.price > 0) {
        this.el.dataset.productPriceCents = String(Math.round(row.price));
      }
      const inst = Number(row.installments ?? 0);
      if (inst > 0) {
        this.el.dataset.minInstallments = String(inst);
      }
    }

    if (this.active) {
      this.active = false;
      this.button?.setAttribute('aria-pressed', 'false');
      this.el.removeAttribute('data-active');
      this.removeSellingPlanInput();
    }

    this.resolveSellingPlan();

    ltvBpDebugLog('onCoaVariantChange', {
      variantId,
      wasActive,
      sellingPlanGid: this.sellingPlanGid,
      minInstallments: this.minInstallments,
    });

    if (wasActive && this.sellingPlanGid) {
      void this.toggle();
    }
  }
}

let cartUpdateSyncTimer = null;

function scheduleLtvActiveToggleSync() {
  clearTimeout(cartUpdateSyncTimer);
  cartUpdateSyncTimer = setTimeout(() => {
    cartUpdateSyncTimer = null;
    document.querySelectorAll(`${LIVE_TV_ROOT_SELECTOR} ${TOGGLE_SELECTOR}[data-active]`).forEach((el) => {
      const inst = el._budgetPayToggleInstance;
      if (inst instanceof LiveTvBudgetPayToggle) {
        syncBudgetPayStateWithContext(inst.getSyncContext()).catch((err) => {
          console.error('[BudgetPay][LTV] cart:update sync', err);
          showBudgetPayError(
            err instanceof Error ? err.message : 'Could not sync Budget Pay with your cart.',
            inst.el,
          );
        });
      }
    });
  }, CART_UPDATE_SYNC_DEBOUNCE_MS);
}

function initLiveTvTogglesInRoot(root) {
  root.querySelectorAll(TOGGLE_SELECTOR).forEach((toggleEl) => {
    if (toggleEl.dataset.ltvBpToggleInit) return;
    const article = toggleEl.closest('article.currently-on-air-card');
    if (!article) return;
    toggleEl.dataset.ltvBpToggleInit = 'true';
    new LiveTvBudgetPayToggle(toggleEl, article);
  });
}

function moveBuyerConsentInArticle(article) {
  const consent = article.querySelector('#shopify-buyer-consent');
  const ctaButtons = article.querySelector('.currently-on-air-card__cta-buttons');
  if (!consent || !ctaButtons) return;
  /* Express payments have their own line under Add to Basket, so the consent text goes under them. */
  const anchor = article.querySelector('.currently-on-air-card__payment-buttons') || ctaButtons;
  if (anchor.nextElementSibling === consent) return;
  if (
    (ctaButtons.parentElement && !ctaButtons.parentElement.contains(consent)) ||
    consent.closest('.currently-on-air-card__cta-buttons, .currently-on-air-card__payment-buttons, .shopify-payment-button') ||
    anchor !== ctaButtons
  ) {
    anchor.after(consent);
  }
}

function initBuyerConsentObserver() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const el = node.id === 'shopify-buyer-consent' ? node : node.querySelector?.('#shopify-buyer-consent');
        if (el) {
          const article = el.closest('article.currently-on-air-card');
          if (article) moveBuyerConsentInArticle(article);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function initLiveTvPage() {
  mergeBpSellingPlansFromDomIntoBody();
  document.querySelectorAll(LIVE_TV_ROOT_SELECTOR).forEach((root) => {
    initLiveTvTogglesInRoot(root);
    root.querySelectorAll('article.currently-on-air-card').forEach(moveBuyerConsentInArticle);
  });
}

function onVariantSelectChange(ev) {
  const sel = ev.target;
  if (!(sel instanceof HTMLSelectElement) || !sel.matches('select[data-variant-select]')) return;
  const root = sel.closest(LIVE_TV_ROOT_SELECTOR);
  if (!root) return;
  const article = sel.closest('article.currently-on-air-card');
  if (!article) return;
  const toggle = article.querySelector(TOGGLE_SELECTOR);
  const inst = toggle?._budgetPayToggleInstance;
  if (!(inst instanceof LiveTvBudgetPayToggle)) return;

  const form = sel.closest('form');
  const hidden = form?.querySelector('input[name="id"][data-current-variant-id], input[name="id"]');
  const vid = hidden?.value || sel.value;
  if (!vid) return;
  queueMicrotask(() => {
    const v2 = hidden?.value || sel.value;
    if (v2) inst.onCoaVariantChange(v2);
  });
}

function init() {
  initLiveTvPage();
  initBuyerConsentObserver();

  document.addEventListener('change', onVariantSelectChange, true);

  document.addEventListener('cart:update', () => {
    scheduleLtvActiveToggleSync();
  });

  document.addEventListener('shopify:section:load', () => {
    initLiveTvPage();
  });
}

/**
 * Re-run plan merge + toggle wiring after COA HTML swap (PubNub / poll).
 * @param {ParentNode} [slotEl] — e.g. [data-currently-on-air-slot]
 */
/**
 * @param {HTMLFormElement | null | undefined} form
 * @returns {LiveTvBudgetPayToggle | null}
 */
function getToggleForForm(form) {
  const article = form?.closest?.('article.currently-on-air-card');
  if (!article) return null;
  const toggleHost = article.querySelector(TOGGLE_SELECTOR);
  const inst = toggleHost?._budgetPayToggleInstance;
  return inst instanceof LiveTvBudgetPayToggle ? inst : null;
}

/**
 * Submit-time BP sync (mirrors PDP BudgetPayCart.syncBudgetPayState).
 * @param {HTMLFormElement | null | undefined} form
 */
async function syncBudgetPayStateForForm(form) {
  const inst = getToggleForForm(form);
  if (!inst || !inst.active || !inst.el?.hasAttribute('data-active')) return;
  await syncBudgetPayStateWithContext(inst.getSyncContext());
}

function reinit(slotEl) {
  mergeBpSellingPlansFromDomIntoBody();
  const root =
    slotEl?.closest?.(LIVE_TV_ROOT_SELECTOR) ||
    (slotEl instanceof Element ? slotEl.querySelector(LIVE_TV_ROOT_SELECTOR) : null) ||
    document.querySelector(LIVE_TV_ROOT_SELECTOR);
  if (root) {
    mergeBpSellingPlansFromDomIntoBody(root);
    initLiveTvTogglesInRoot(root);
    root.querySelectorAll('article.currently-on-air-card').forEach(moveBuyerConsentInArticle);
  } else {
    initLiveTvPage();
  }
}

window.BudgetPayLiveTv = {
  reinit,
  initLiveTvPage,
  getToggleForForm,
  syncBudgetPayStateForForm,
  /** Turn on verbose LTV BP logging (persists in localStorage). */
  enableDebug() {
    try {
      window.localStorage.setItem(LTV_BP_DEBUG_KEY, '1');
      console.log('[BudgetPay][LTV] Debug enabled. Reload and reproduce. Disable: BudgetPayLiveTv.disableDebug()');
    } catch (e) {
      console.warn('[BudgetPay][LTV] Could not enable debug', e);
    }
  },
  disableDebug() {
    try {
      window.localStorage.removeItem(LTV_BP_DEBUG_KEY);
      console.log('[BudgetPay][LTV] Debug disabled.');
    } catch (e) {
      console.warn('[BudgetPay][LTV] Could not disable debug', e);
    }
  },
  isDebugEnabled: isLtvBpDebugEnabled,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
